class CollectionShowcaseSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.interval = null;
    this.syncTimers = {};
    this.inFlight = {};
    this.needsSync = {};
    this.cartQuantities = {};
    this.cartUpdateUnsubscriber = null;
    this.externalCartChangeHandler = this.handleExternalCartQuantityChange.bind(this);
    this.externalCartRefreshTimers = [];
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-showcase-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-showcase-dot]'));
    this.delay = Number(this.dataset.autoplayDelay || 3000);

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goTo(index);
        this.restart();
      });
    });

    this.querySelectorAll('[data-showcase-add]').forEach((button) => {
      button.addEventListener('click', this.incrementQuantity.bind(this));
    });

    this.querySelectorAll('[data-showcase-quantity]').forEach((input) => {
      input.addEventListener('input', this.queueQuantitySync.bind(this));
      input.addEventListener('change', this.queueQuantitySync.bind(this));
    });

    this.cacheRenderedQuantities();
    this.refreshCartQuantities();
    this.subscribeToCartUpdates();
    document.addEventListener('change', this.externalCartChangeHandler, true);

    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.cartUpdateUnsubscriber?.();
    document.removeEventListener('change', this.externalCartChangeHandler, true);
    this.externalCartRefreshTimers.forEach((timer) => window.clearTimeout(timer));
  }

  start() {
    if (this.slides.length < 2 || this.interval) return;
    this.interval = window.setInterval(() => this.next(), this.delay);
  }

  stop() {
    window.clearInterval(this.interval);
    this.interval = null;
  }

  restart() {
    this.stop();
    this.start();
  }

  next() {
    this.goTo((this.currentIndex + 1) % this.slides.length);
  }

  goTo(index) {
    this.slides[this.currentIndex]?.classList.remove('is-active');
    this.dots[this.currentIndex]?.classList.remove('is-active');
    this.currentIndex = index;
    this.slides[this.currentIndex]?.classList.add('is-active');
    this.dots[this.currentIndex]?.classList.add('is-active');
    this.applyCartQuantitiesToInputs();
  }

  incrementQuantity(event) {
    const input = event.currentTarget.closest('[data-showcase-product]')?.querySelector('[data-showcase-quantity]');
    if (!input) return;

    input.value = String(Math.max(Number(input.value || 0), 0) + 1);
    this.queueQuantitySync({ currentTarget: input }, 120);
  }

  queueQuantitySync(event, delay = 320) {
    const context = this.getVariantContext(event.currentTarget);
    if (!context) return;

    const quantity = Math.max(Number(context.input.value || 0), 0);
    context.input.value = String(quantity);
    context.input.setAttribute('value', String(quantity));
    this.cartQuantities[context.variantId] = quantity;

    window.clearTimeout(this.syncTimers[context.variantId]);
    this.syncTimers[context.variantId] = window.setTimeout(() => {
      this.flushQuantity(context.variantId, context.input);
    }, delay);
  }

  getVariantContext(input) {
    const product = input.closest('[data-showcase-product]');
    const button = product?.querySelector('[data-showcase-add]');
    const variantId = button?.dataset.variantId;

    if (!variantId) return null;

    return { button, input, variantId };
  }

  async flushQuantity(variantId, input) {
    const context = this.getVariantContext(input);
    if (!context) return;

    if (this.inFlight[variantId]) {
      this.needsSync[variantId] = true;
      return;
    }

    const quantity = Math.max(Number(context.input.value || 0), 0);
    input.value = String(quantity);
    input.setAttribute('value', String(quantity));
    this.cartQuantities[variantId] = quantity;
    context.button.classList.remove('is-added');
    context.button.classList.add('is-syncing');

    const body = JSON.stringify({
      updates: {
        [variantId]: quantity,
      },
      sections: this.getCartSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    this.inFlight[variantId] = true;

    try {
      const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body });
      const parsedState = await response.json();

      if (parsedState.status) throw new Error(parsedState.description || parsedState.message);

      this.renderCartSections(parsedState);
      const currentQuantity = Math.max(Number(input.value || 0), 0);
      if (currentQuantity === quantity) {
        this.updateCartQuantitiesFromCart(parsedState);
        this.syncMatchingInputs(variantId, quantity);
      } else {
        this.needsSync[variantId] = true;
      }
      this.dispatchCartRefreshEvents(parsedState);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'collection-showcase', cartData: parsedState });
      }

      context.button.classList.add('is-added');
      window.setTimeout(() => context.button.classList.remove('is-added'), 800);
    } catch (error) {
      console.error('Collection showcase cart update failed:', error);
    } finally {
      context.button.classList.remove('is-syncing');
      this.inFlight[variantId] = false;

      if (this.needsSync[variantId]) {
        this.needsSync[variantId] = false;
        this.queueQuantitySync({ currentTarget: input }, 80);
      }
    }
  }

  syncMatchingInputs(variantId, quantity) {
    this.querySelectorAll(`[data-showcase-add][data-variant-id="${variantId}"]`).forEach((button) => {
      const input = button.closest('[data-showcase-product]')?.querySelector('[data-showcase-quantity]');
      if (input) {
        input.value = String(quantity);
        input.setAttribute('value', String(quantity));
      }
    });
  }

  cacheRenderedQuantities() {
    this.querySelectorAll('[data-showcase-quantity][data-variant-id]').forEach((input) => {
      this.cartQuantities[input.dataset.variantId] = Math.max(Number(input.value || 0), 0);
    });
  }

  subscribeToCartUpdates() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.cartData) {
        this.updateCartQuantitiesFromCart(event.cartData);
        this.applyCartQuantitiesToInputs();
      }
    });
  }

  handleExternalCartQuantityChange(event) {
    if (this.contains(event.target)) return;
    if (!event.target.closest('quick-add-bulk, quantity-input, cart-items')) return;

    this.externalCartRefreshTimers.forEach((timer) => window.clearTimeout(timer));
    this.externalCartRefreshTimers = [500, 1400].map((delay) =>
      window.setTimeout(() => this.refreshCartQuantities(), delay)
    );
  }

  refreshCartQuantities() {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        this.updateCartQuantitiesFromCart(cart);
        this.applyCartQuantitiesToInputs();
      })
      .catch((error) => {
        console.error('Collection showcase cart quantity refresh failed:', error);
      });
  }

  updateCartQuantitiesFromCart(cart) {
    const nextQuantities = {};
    (cart.items || []).forEach((item) => {
      nextQuantities[String(item.variant_id || item.id)] = item.quantity;
    });
    this.cartQuantities = nextQuantities;
  }

  applyCartQuantitiesToInputs() {
    this.querySelectorAll('[data-showcase-quantity][data-variant-id]').forEach((input) => {
      const variantId = input.dataset.variantId;
      if (this.inFlight[variantId] || this.needsSync[variantId]) return;
      if (document.activeElement === input) return;

      const quantity = this.cartQuantities[variantId] || 0;
      input.value = String(quantity);
      input.setAttribute('value', String(quantity));
    });
  }

  getCartSectionsToRender() {
    return [
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }

  renderCartSections(parsedState) {
    if (!parsedState.sections) return;

    document.querySelector('cart-drawer')?.classList.toggle('is-empty', parsedState.item_count === 0);

    this.getCartSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id);
      if (!sectionElement || !parsedState.sections[section.section]) return;

      const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
      const source = new DOMParser()
        .parseFromString(parsedState.sections[section.section], 'text/html')
        .querySelector(section.selector || '.shopify-section');

      if (elementToReplace && source) {
        elementToReplace.innerHTML = source.innerHTML;
      }
    });
  }

  dispatchCartRefreshEvents(parsedState) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: parsedState } }));
    document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: parsedState } }));
    window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: parsedState } }));
  }
}

if (!customElements.get('collection-showcase-slider')) {
  customElements.define('collection-showcase-slider', CollectionShowcaseSlider);
}
